import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import CircularProgress from "@mui/material/CircularProgress";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";
import PeopleOutlinedIcon from "@mui/icons-material/PeopleOutlined";
import DatasetOutlinedIcon from "@mui/icons-material/DatasetOutlined";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import OpenInNewOutlined from "@mui/icons-material/OpenInNewOutlined";
import PieChartOutlined from "@mui/icons-material/PieChartOutlined";
import SwapHorizOutlined from "@mui/icons-material/SwapHorizOutlined";
import MemoryOutlined from "@mui/icons-material/MemoryOutlined";
import type {
  LiqIntelIdentityAnalysisKey,
  LiqIntelReportPayload,
  LiqIntelSourceLink,
} from "../types/liqIntelReportPayload";
import staticReportPayloadJson from "../types/liqIntelReportPayload.full.example.json";
import { API_BASE_URL } from "../config/api";
import { LiqIntelProseOrBullets } from "../components/LiqIntelProseOrBullets";
import {
  LiqIntelDelinquencyTable,
  type DelinquencyRow,
} from "../components/LiqIntelDelinquencyTable";
import "./LiqIntelReport.css";

const STATIC_REPORT_PAYLOAD = staticReportPayloadJson as LiqIntelReportPayload;

function IdentitySourceLinkChips({
  sources,
  variant = "analysis",
}: {
  sources?: LiqIntelSourceLink[] | null;
  variant?: "analysis" | "narrative";
}) {
  const list = Array.isArray(sources) ? sources : [];
  if (list.length === 0) return null;
  const chipClass =
    variant === "narrative"
      ? "liq-source-chip liq-source-chip--identity-narrative"
      : "liq-source-chip liq-source-chip--identity-analysis";
  return (
    <div className="liq-source-list liq-source-list--identity">
      {list.map((source, idx) => (
        <a
          key={`${source.url}-${idx}`}
          href={source.url}
          target="_blank"
          rel="noopener noreferrer"
          className={chipClass}
        >
          <OpenInNewOutlined className="liq-source-chip-icon" aria-hidden />
          {source.label}
        </a>
      ))}
    </div>
  );
}

function AccordionSectionTitle({
  Icon,
  children,
  tone = "default",
  trailing,
}: {
  Icon: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
  tone?: "default" | "orange";
  trailing?: React.ReactNode;
}) {
  const iconClass =
    tone === "orange"
      ? "liq-card-title-icon liq-card-title-icon--orange"
      : "liq-card-title-icon";
  return (
    <div className="liq-card-title-row">
      <span className="liq-card-title-icon-wrap" aria-hidden>
        <Icon className={iconClass} />
      </span>
      <div
        className={
          trailing
            ? "liq-card-title-heading liq-card-title-heading--with-trailing"
            : "liq-card-title-heading"
        }
      >
        <h2>{children}</h2>
        {trailing}
      </div>
    </div>
  );
}

type SectionKey =
  | "classification"
  | "identity"
  | "services"
  | "contacts"
  | "datasources";

const SECTION_LABELS: Record<SectionKey, string> = {
  classification: "Classification",
  identity: "Company Profile",
  services: "Required Services",
  contacts: "Key Contacts",
  datasources: "Data Sources",
};

type IdentitySubKey = LiqIntelIdentityAnalysisKey;

type IdentityTopCard =
  | { label: string; value: string; valueBold?: boolean }
  | { label: string; regions: string[] };

function identityTopCardsFromPayload(
  identity: LiqIntelReportPayload["identity"]
): IdentityTopCard[] {
  return [
    { label: "Legal Name", value: identity.legalName, valueBold: true },
    { label: "Headquarters", value: identity.headquarters, valueBold: true },
    { label: "Operating Regions", regions: identity.operatingRegions },
  ];
}

type ServiceItem = LiqIntelReportPayload["services"][number] & { number: number };

type LiqIntelAnalyzeResult = {
  payload: LiqIntelReportPayload;
  model?: string;
  delinquencyRows?: DelinquencyRow[];
};

const reportPayloadPromiseCache = new Map<string, Promise<LiqIntelAnalyzeResult>>();
const reportPayloadDataCache = new Map<string, LiqIntelAnalyzeResult>();

function liqIntelAnalyzeCacheKey(companyName: string, provider: string): string {
  return `${companyName}\u0000${provider || "__default__"}`;
}

async function fetchLiqIntelReport(
  companyName: string,
  provider: string,
  cert?: string
): Promise<LiqIntelAnalyzeResult> {
  const cacheKey = `${liqIntelAnalyzeCacheKey(companyName, provider)}\u0000${cert || "__no_cert__"}`;
  const cached = reportPayloadDataCache.get(cacheKey);
  if (cached) return cached;

  const pending = reportPayloadPromiseCache.get(cacheKey);
  if (pending) return pending;

  const analyzeUrl = `${API_BASE_URL}/api/liq-intel/analyze`;
  const body: { companyName: string; provider?: string; cert?: string } = { companyName };
  if (provider === "openai" || provider === "perplexity" || provider === "gemini") {
    body.provider = provider;
  }
  if (cert && /^\d{1,10}$/.test(cert)) {
    body.cert = cert;
  }
  const request = fetch(analyzeUrl, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  })
    .then(async (r) => {
      const text = await r.text();
      let data: unknown;
      try {
        data = text ? (JSON.parse(text) as unknown) : {};
      } catch {
        throw new Error(text || r.statusText || "Invalid response");
      }
      if (!r.ok) {
        const errMsg =
          typeof data === "object" &&
          data !== null &&
          "error" in data &&
          typeof (data as { error: unknown }).error === "string"
            ? (data as { error: string }).error
            : text || r.statusText || "Request failed";
        throw new Error(errMsg);
      }
      const payload =
        typeof data === "object" &&
        data !== null &&
        "payload" in data &&
        (data as { payload: LiqIntelReportPayload }).payload !== undefined
          ? (data as { payload: LiqIntelReportPayload }).payload
          : (data as LiqIntelReportPayload);
      const model =
        typeof data === "object" &&
        data !== null &&
        "model" in data &&
        typeof (data as { model: unknown }).model === "string"
          ? (data as { model: string }).model.trim()
          : undefined;
      const delinquencyRows =
        typeof data === "object" &&
        data !== null &&
        "delinquencyRows" in data &&
        Array.isArray((data as { delinquencyRows: unknown }).delinquencyRows)
          ? ((data as { delinquencyRows: DelinquencyRow[] }).delinquencyRows ?? [])
          : undefined;
      const result: LiqIntelAnalyzeResult = { payload, model, delinquencyRows };
      reportPayloadDataCache.set(cacheKey, result);
      return result;
    })
    .finally(() => {
      reportPayloadPromiseCache.delete(cacheKey);
    });

  reportPayloadPromiseCache.set(cacheKey, request);
  return request;
}

const IDENTITY_NESTED_ACCORDIONS: {
  key: IdentitySubKey;
  title: string;
  subtitle: string;
  Icon: React.ComponentType<{ className?: string }>;
}[] = [
  {
    key: "portfolio",
    title: "Portfolio Characteristics",
    subtitle:
      "Composition of assets, holding structures, and risk profile for the credit portfolio.",
    Icon: PieChartOutlined,
  },
  {
    key: "liquidity",
    title: "Liquidity & Secondary Market Behavior",
    subtitle:
      "Secondary market activity, regulatory context, and valuation needs.",
    Icon: SwapHorizOutlined,
  },
  {
    key: "technology",
    title: "Technology & Operational Model",
    subtitle:
      "Platform usage, data and analytics, and opportunities to streamline execution.",
    Icon: MemoryOutlined,
  },
];

const LiqIntelReport: React.FC = () => {
  const [searchParams] = useSearchParams();
  const companyParam = searchParams.get("company")?.trim() ?? "";
  const certParam = searchParams.get("cert")?.trim() ?? "";
  const providerRaw = searchParams.get("provider")?.trim().toLowerCase() ?? "";
  const analyzeProvider =
    providerRaw === "openai" ||
    providerRaw === "perplexity" ||
    providerRaw === "gemini"
      ? providerRaw
      : "";
  const isAnalyzeMode = companyParam.length > 0;

  const [remotePayload, setRemotePayload] = useState<LiqIntelReportPayload | null>(
    null
  );
  const [analysisModel, setAnalysisModel] = useState<string | null>(null);
  const [delinquencyRows, setDelinquencyRows] = useState<DelinquencyRow[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(isAnalyzeMode);
  const [expanded, setExpanded] = useState<Record<SectionKey, boolean>>({
    classification: true,
    identity: true,
    services: true,
    contacts: true,
    datasources: true,
  });
  const [identityExpanded, setIdentityExpanded] = useState<
    Record<IdentitySubKey, boolean>
  >({
    portfolio: true,
    liquidity: true,
    technology: true,
  });
  const [servicesExpanded, setServicesExpanded] = useState<Record<number, boolean>>(
    {}
  );

  useEffect(() => {
    if (!isAnalyzeMode) {
      setAnalysisModel(null);
      setDelinquencyRows(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    setRemotePayload(null);
    setAnalysisModel(null);
    setDelinquencyRows(null);

    fetchLiqIntelReport(companyParam, analyzeProvider, certParam)
      .then((result) => {
        if (!cancelled) {
          setRemotePayload(result.payload);
          setAnalysisModel(result.model && result.model.length > 0 ? result.model : null);
          setDelinquencyRows(Array.isArray(result.delinquencyRows) ? result.delinquencyRows : null);
        }
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          const msg =
            e instanceof Error ? e.message : typeof e === "string" ? e : "Analysis failed";
          setLoadError(msg);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isAnalyzeMode, companyParam, analyzeProvider, certParam]);

  const reportPayload = useMemo((): LiqIntelReportPayload => {
    if (!isAnalyzeMode) return STATIC_REPORT_PAYLOAD;
    if (remotePayload) return remotePayload;
    return STATIC_REPORT_PAYLOAD;
  }, [isAnalyzeMode, remotePayload]);

  const { hero, classification, identity, contacts, datasources } = reportPayload;
  const kpiCards = useMemo(
    () => identityTopCardsFromPayload(identity),
    [identity]
  );

  const servicesWithNumbers = useMemo(
    () =>
      reportPayload.services.map((s, i) => ({
        ...s,
        number: i + 1,
      })) as ServiceItem[],
    [reportPayload]
  );

  useEffect(() => {
    setServicesExpanded(
      Object.fromEntries(
        reportPayload.services.map((_, i) => [i + 1, false])
      ) as Record<number, boolean>
    );
  }, [reportPayload]);

  const allExpanded = useMemo(
    () => Object.values(expanded).every(Boolean),
    [expanded]
  );

  const toggleAll = () => {
    const next = !allExpanded;
    setExpanded({
      classification: next,
      identity: next,
      services: next,
      contacts: next,
      datasources: next,
    });
  };

  const toggle = (key: SectionKey) =>
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  if (isAnalyzeMode && loadError) {
    return (
      <div className="liq-report-page liq-report-page--status">
        <div className="liq-report-status-card">
          <h1 className="liq-report-status-title">Analysis could not complete</h1>
          <p className="liq-report-status-message">{loadError}</p>
          <p className="liq-report-status-hint">
            Check that the API server is running, the correct provider API key is
            configured (OpenAI, Perplexity, or Gemini), and try again from the
            Liquidity Intelligence page.
          </p>
        </div>
      </div>
    );
  }

  if (isAnalyzeMode && (loading || !remotePayload)) {
    return (
      <div className="liq-report-page liq-report-page--loading">
        <div className="liq-report-loading-panel" role="status" aria-live="polite">
          <CircularProgress size={56} thickness={4} />
          <p className="liq-report-loading-text">
            Analyzing <strong>{companyParam}</strong>
            {analyzeProvider ? (
              <>
                {" "}
                with <strong>{analyzeProvider}</strong>…
              </>
            ) : (
              " …"
            )}
          </p>
          <p className="liq-report-loading-sub">
            Generating intelligence report. This may take a minute.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="liq-report-page">
      <main className="liq-report-main">
        <div className="liq-report-topline">
          <div className="liq-report-brand">
            <img
              src="https://app.soteriamarket.com/banner3.jpeg"
              alt="Soteria logo"
              className="liq-report-logo"
            />
            <div className="liq-report-divider" />
            <span>Liquidity Intelligence</span>
          </div>
          <div className="liq-report-ai">
            <span className="liq-report-ai-label">AI-Powered Analysis</span>
            {isAnalyzeMode && analysisModel ? (
              <span className="liq-report-ai-model" title={analysisModel}>
                · {analysisModel}
              </span>
            ) : null}
          </div>
        </div>

        <header className="liq-report-hero">
          <div className="liq-report-hero-head">
            <AccountBalanceOutlinedIcon
              className="liq-report-hero-title-icon"
              aria-hidden
            />
            <div className="liq-report-hero-title-row">
              <h1>{hero.companyName}</h1>
              <span className="liq-badge">{hero.roleBadge}</span>
            </div>
            <p className="liq-report-hero-date">
              <CalendarTodayOutlinedIcon
                className="liq-report-hero-date-icon"
                aria-hidden
              />
              {hero.generatedAt}
            </p>
          </div>
          <nav className="liq-nav">
            {Object.entries(SECTION_LABELS).map(([key, label]) => (
              <a key={key} href={`#${key}`}>
                {label}
              </a>
            ))}
          </nav>
        </header>

        <div className="liq-actions">
          <button onClick={toggleAll} type="button" className="liq-actions-toggle">
            {allExpanded ? (
              <UnfoldLessIcon className="liq-actions-toggle-icon" />
            ) : (
              <UnfoldMoreIcon className="liq-actions-toggle-icon" />
            )}
            {allExpanded ? "Collapse All Sections" : "Expand All Sections"}
          </button>
        </div>

        <Accordion
          id="classification"
          className="liq-card"
          expanded={expanded.classification}
          onChange={() => toggle("classification")}
          disableGutters
          elevation={0}
        >
          <AccordionSummary className="liq-card-head" expandIcon={<ExpandMoreIcon />}>
            <AccordionSectionTitle
              Icon={CategoryOutlinedIcon}
              trailing={
                <span className="liq-classification-role-chip">{hero.roleBadge}</span>
              }
            >
              Lead Classification
            </AccordionSectionTitle>
          </AccordionSummary>
          <AccordionDetails className="liq-card-body">
              <p className="liq-section-intro">
                Rationale for classification based on verified market activity and
                public data:
              </p>
              <div className="liq-classification-list">
                {classification.items.map((item, index) => (
                  <article key={index} className="liq-classification-item">
                    <div className="liq-classification-number">{index + 1}</div>
                    <div className="liq-classification-content">
                      <LiqIntelProseOrBullets
                        text={item.text}
                        className="liq-classification-body"
                      />
                      <div className="liq-source-list">
                        {item.sources.map((source) => (
                          <a
                            key={source.url}
                            href={source.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="liq-source-chip liq-source-chip--classification"
                          >
                            <OpenInNewOutlined
                              className="liq-source-chip-icon"
                              aria-hidden
                            />
                            {source.label}
                          </a>
                        ))}
                      </div>
                    </div>
                  </article>
                ))}
              </div>
          </AccordionDetails>
        </Accordion>

        <Accordion
          id="identity"
          className="liq-card"
          expanded={expanded.identity}
          onChange={() => toggle("identity")}
          disableGutters
          elevation={0}
        >
          <AccordionSummary className="liq-card-head" expandIcon={<ExpandMoreIcon />}>
            <AccordionSectionTitle Icon={BusinessOutlinedIcon}>
              Company Profile &amp; Analysis
            </AccordionSectionTitle>
          </AccordionSummary>
          <AccordionDetails className="liq-card-body">
              <div className="liq-identity-grid">
                {kpiCards.map((card) => (
                  <article key={card.label} className="liq-identity-kpi">
                    <p className="liq-kpi-label">{card.label}</p>
                    {"regions" in card ? (
                      <div className="liq-source-list liq-identity-regions-chips">
                        {card.regions.map((region) => (
                          <span key={region} className="liq-source-chip">
                            {region}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <LiqIntelProseOrBullets
                        text={card.value}
                        className={
                          card.valueBold
                            ? "liq-kpi-value liq-kpi-value--bold"
                            : "liq-kpi-value"
                        }
                      />
                    )}
                  </article>
                ))}
              </div>

              <article className="liq-identity-block">
                <p className="liq-kpi-label">Business Model</p>
                <LiqIntelProseOrBullets
                  text={identity.businessModel}
                  className="liq-kpi-value"
                />
                <IdentitySourceLinkChips
                  sources={identity.businessModelSources}
                  variant="narrative"
                />
              </article>

              <article className="liq-identity-block liq-identity-highlight">
                <p className="liq-kpi-label">Scale of Credit Activities</p>
                <LiqIntelProseOrBullets
                  text={identity.scaleOfCreditActivities}
                  className="liq-kpi-value"
                />
                <IdentitySourceLinkChips
                  sources={identity.scaleOfCreditActivitiesSources}
                  variant="narrative"
                />
              </article>

              {isAnalyzeMode && delinquencyRows && delinquencyRows.length > 0 ? (
                <LiqIntelDelinquencyTable rows={delinquencyRows} />
              ) : null}

              <article className="liq-identity-block liq-identity-segments">
                <p className="liq-kpi-label">Active Credit Segments</p>
                <div className="liq-source-list">
                  {identity.activeCreditSegments.map((segment) => (
                    <span key={segment} className="liq-source-chip">
                      {segment}
                    </span>
                  ))}
                </div>
              </article>

              <div className="liq-nested">
                {IDENTITY_NESTED_ACCORDIONS.map(({ key, title, subtitle, Icon }) => (
                  <Accordion
                    key={key}
                    className="liq-nested-accordion"
                    expanded={identityExpanded[key]}
                    onChange={(_e, isExpanded) =>
                      setIdentityExpanded((prev) => ({ ...prev, [key]: isExpanded }))
                    }
                    disableGutters
                    elevation={0}
                  >
                    <AccordionSummary
                      className="liq-nested-accordion-summary"
                      expandIcon={<ExpandMoreIcon />}
                    >
                      <div className="liq-nested-accordion-summary-inner">
                        <span className="liq-nested-accordion-icon-wrap" aria-hidden>
                          <Icon className="liq-nested-accordion-icon" />
                        </span>
                        <div className="liq-nested-accordion-head-text">
                          <span className="liq-nested-accordion-title">{title}</span>
                          <span className="liq-nested-accordion-subtitle">{subtitle}</span>
                        </div>
                      </div>
                    </AccordionSummary>
                    <AccordionDetails className="liq-nested-accordion-details">
                      {identity.analysis[key].map((item, idx) => (
                        <div key={`${key}-${idx}-${item.title}`} className="liq-nested-item">
                          <p className="liq-kpi-label">{item.title}</p>
                          <LiqIntelProseOrBullets
                            text={item.body}
                            className="liq-nested-item-body"
                          />
                          <IdentitySourceLinkChips sources={item.sources} variant="analysis" />
                        </div>
                      ))}
                    </AccordionDetails>
                  </Accordion>
                ))}
              </div>
          </AccordionDetails>
        </Accordion>

        <Accordion
          id="services"
          className="liq-card"
          expanded={expanded.services}
          onChange={() => toggle("services")}
          disableGutters
          elevation={0}
        >
          <AccordionSummary className="liq-card-head" expandIcon={<ExpandMoreIcon />}>
            <AccordionSectionTitle
              Icon={BuildOutlinedIcon}
              tone="orange"
              trailing={
                <span className="liq-services-count-chip">
                  {servicesWithNumbers.length} Services Identified
                </span>
              }
            >
              Required Services
            </AccordionSectionTitle>
          </AccordionSummary>
          <AccordionDetails className="liq-card-body">
              <p className="liq-section-intro">
                Soteria Market services aligned to this target company's credit
                activities and liquidity needs:
              </p>
              <div className="liq-services-grid">
                {servicesWithNumbers.map((service) => (
                  <article key={service.number} className="liq-service-card">
                    <div className="liq-service-main">
                      <div className="liq-service-number">{service.number}</div>
                      <div>
                        <h3>{service.title}</h3>
                        <LiqIntelProseOrBullets
                          text={service.summary}
                          className="liq-service-summary"
                        />
                      </div>
                    </div>
                    <Accordion
                      className="liq-service-evidence-accordion"
                      expanded={Boolean(servicesExpanded[service.number])}
                      onChange={(_e, isExpanded) =>
                        setServicesExpanded((prev) => ({
                          ...prev,
                          [service.number]: isExpanded,
                        }))
                      }
                      disableGutters
                      elevation={0}
                    >
                      <AccordionSummary
                        className="liq-service-evidence-accordion-summary"
                        expandIcon={<ExpandMoreIcon />}
                      >
                        <div className="liq-service-evidence-summary-inner">
                          <span className="liq-service-evidence-summary-label">
                            Evidence &amp; Sources
                          </span>
                          <span className="liq-service-evidence-count-chip">
                            {service.evidence.length}
                          </span>
                        </div>
                      </AccordionSummary>
                      <AccordionDetails className="liq-service-evidence-accordion-details">
                        <div className="liq-service-evidence">
                          {service.evidence.map((ev, idx) => (
                            <div key={idx} className="liq-service-evidence-item">
                              <LiqIntelProseOrBullets
                                text={ev.text}
                                className="liq-service-evidence-text"
                              />
                              <div className="liq-source-list">
                                {ev.sources.map((source) => (
                                  <a
                                    key={source.url}
                                    href={source.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="liq-source-chip liq-source-chip--service-evidence"
                                  >
                                    <OpenInNewOutlined
                                      className="liq-source-chip-icon"
                                      aria-hidden
                                    />
                                    {source.label}
                                  </a>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </AccordionDetails>
                    </Accordion>
                  </article>
                ))}
              </div>
          </AccordionDetails>
        </Accordion>

        <Accordion
          id="contacts"
          className="liq-card"
          expanded={expanded.contacts}
          onChange={() => toggle("contacts")}
          disableGutters
          elevation={0}
        >
          <AccordionSummary className="liq-card-head" expandIcon={<ExpandMoreIcon />}>
            <AccordionSectionTitle
              Icon={PeopleOutlinedIcon}
              trailing={
                <span className="liq-contacts-title-chip">
                  {contacts.length} Identified
                </span>
              }
            >
              Key Contacts
            </AccordionSectionTitle>
          </AccordionSummary>
          <AccordionDetails className="liq-card-body">
              <p className="liq-section-intro">
                Decision-makers and influencers for liquidity and secondary credit
                trading at the target company:
              </p>

              <div className="liq-contacts-desktop">
                <table className="liq-contacts-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Title</th>
                      <th>Business Unit</th>
                      <th>Location</th>
                      <th>Source</th>
                    </tr>
                  </thead>
                  <tbody>
                    {contacts.map((contact) => (
                      <tr key={contact.name}>
                        <td className="liq-contacts-name">{contact.name}</td>
                        <td>{contact.title}</td>
                        <td>{contact.unit}</td>
                        <td>{contact.location}</td>
                        <td>
                          <a
                            href={contact.source.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="liq-source-chip liq-source-chip--contact"
                          >
                            <OpenInNewOutlined
                              className="liq-source-chip-icon"
                              aria-hidden
                            />
                            {contact.source.label}
                          </a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="liq-contacts-mobile">
                {contacts.map((contact) => (
                  <article key={`${contact.name}-mobile`} className="liq-contact-card">
                    <div className="liq-contact-head">
                      <div>
                        <p className="liq-contact-name">{contact.name}</p>
                        <p className="liq-contact-title">{contact.title}</p>
                      </div>
                      <a
                        href={contact.source.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="liq-source-chip liq-source-chip--contact"
                      >
                        <OpenInNewOutlined
                          className="liq-source-chip-icon"
                          aria-hidden
                        />
                        {contact.source.label}
                      </a>
                    </div>
                    <div className="liq-contact-meta">
                      <span>{contact.unit}</span>
                      <span>{contact.location}</span>
                    </div>
                  </article>
                ))}
              </div>
          </AccordionDetails>
        </Accordion>

        <Accordion
          id="datasources"
          className="liq-card"
          expanded={expanded.datasources}
          onChange={() => toggle("datasources")}
          disableGutters
          elevation={0}
        >
          <AccordionSummary className="liq-card-head" expandIcon={<ExpandMoreIcon />}>
            <AccordionSectionTitle Icon={DatasetOutlinedIcon}>
              Recommended Data &amp; Subscription Sources
            </AccordionSectionTitle>
          </AccordionSummary>
          <AccordionDetails className="liq-card-body">
              <p className="liq-section-intro">
                These subscriptions can significantly enhance coverage and
                validation of the intelligence in this report:
              </p>
              <div className="liq-datasource-grid">
                {datasources.map((card) => (
                  <article key={card.title} className="liq-datasource-card">
                    <div className="liq-datasource-head">
                      <h3>{card.title}</h3>
                      <div className="liq-datasource-links">
                        {card.links.map((link) => (
                          <a
                            key={link.url}
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="liq-source-chip liq-source-chip--datasource"
                          >
                            <OpenInNewOutlined
                              className="liq-source-chip-icon"
                              aria-hidden
                            />
                            <span className="liq-datasource-chip-label">
                              {link.label}
                            </span>
                          </a>
                        ))}
                      </div>
                    </div>
                    <LiqIntelProseOrBullets
                      text={card.description}
                      className="liq-datasource-description"
                    />
                  </article>
                ))}
              </div>
          </AccordionDetails>
        </Accordion>
      </main>
    </div>
  );
};

export default LiqIntelReport;
